import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { FormattedUTCDate } from '@swf/poker';

import Table from '../../../../components/Table/Table';
import { PKR_LOADERS, PKR_USER_ROLE_TRANSLATIONS } from '../../../../constants';
import useCheckUserClubRole from '../../../../hooks/useCheckUserClubRole';
import { useHomeGamesWidget } from '../../../../model';
import { ClubPlayerRole, ClubPlayerStatus, UserClubRole } from '../../../../types/common';
import styles from '../shared.scss';
import type { Member } from '../utils';
import { MemberStatusesPerPage, columns } from '../utils';

import ActionButtons from './ActionButtons';

const getRowClassName = (row: Member) => {
  if (row?.membershipStatus === ClubPlayerStatus.SUSPENDED) {
    return styles.suspended;
  }
};

const Approved: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const isAdminOrManager = useCheckUserClubRole(clubId, [UserClubRole.Admin, UserClubRole.Manager]);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const loadingState = useSelector(widgetSelect.loading);
  const { approved } = useSelector(widgetSelect.clubMembers);
  const loading = loadingState[PKR_LOADERS.clubMembers];

  useEffect(() => {
    widgetDispatch.fetchClubMembers({
      clubId,
      statuses: MemberStatusesPerPage.Approved,
      memberType: 'approved'
    });

    return () => {
      widgetDispatch.setLoading({ isLoading: true, loaderName: PKR_LOADERS.clubInfo });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const transformedMembers = useMemo(
    () =>
      approved.map((member) => {
        const translation =
          member.membershipStatus === ClubPlayerStatus.SUSPENDED
            ? PKR_USER_ROLE_TRANSLATIONS[ClubPlayerRole.SUSPENDED]
            : member.memberRole !== undefined && member.memberRole !== ClubPlayerRole.SHADOW_FOUNDER
            ? PKR_USER_ROLE_TRANSLATIONS[member.memberRole]
            : { id: '', defaultMessage: '' };
        return {
          ...member,
          enrolled: (
            <FormattedUTCDate utcDate={`${member.enrolled}`} options={{ dateStyle: 'long' }} />
          ),
          role: <Translation {...translation} />
        };
      }),
    [approved]
  );

  return (
    <div className={classNames(styles.approved, { [styles.approvedSelectable]: isAdminOrManager })}>
      <Table
        columns={columns}
        data={transformedMembers}
        selectable={isAdminOrManager}
        sortable={false}
        actionButtons={ActionButtons}
        loading={loading}
        enableMobileSlider
        rowClassName={getRowClassName}
        type="approved"
      />
    </div>
  );
};

export default Approved;
